/**
 * The editor's visual language.
 *
 * Jot shows markdown *source* — the `#` and the `**` stay on screen — but the
 * markers recede and the content they describe is styled. That means every rule
 * here is deliberately low-contrast for syntax and full-contrast for prose.
 *
 * Two things are worth knowing before editing this file:
 *
 * 1. No colour is written literally. Everything resolves a custom property from
 *    styles.css, so light/dark switching is handled by the `dark` class on
 *    <html> and this module never has to know which theme is active.
 *
 * 2. Highlight tags are mapped to *static* class names rather than to inline
 *    style objects. `HighlightStyle` ignores style properties as soon as a
 *    `class` is given, which is exactly what we want: the CSS lives below in one
 *    ordered block, so the cascade is something we control instead of something
 *    we hope for. `@lezer/markdown` attaches several tags to a single span (a
 *    `**` inside a list item carries `list`, `strong` and `processingInstruction`
 *    at once) and only real selectors can express that.
 */

import { HighlightStyle, syntaxHighlighting, syntaxTree } from '@codemirror/language'
import type { Extension, Range } from '@codemirror/state'
import {
  Decoration,
  EditorView,
  ViewPlugin,
  type DecorationSet,
  type ViewUpdate,
} from '@codemirror/view'
import { tags } from '@lezer/highlight'

/* ------------------------------------------------------------------ surface */

export function editorTheme(opts: { theme: 'light' | 'dark'; fontSize: number }): Extension {
  return EditorView.theme(
    {
      '&': {
        height: '100%',
        fontSize: `${opts.fontSize}px`,
        backgroundColor: 'transparent',
        color: 'var(--fg)',
      },
      '&.cm-focused': { outline: 'none' },

      // `inherit` matters: the whole app is already monospace and the editor
      // must not introduce a second family.
      '.cm-scroller': {
        fontFamily: 'inherit',
        lineHeight: '1.65',
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--border-strong) transparent',
      },
      '.cm-scroller::-webkit-scrollbar': { width: '10px', height: '10px' },
      '.cm-scroller::-webkit-scrollbar-thumb': {
        backgroundColor: 'var(--border-strong)',
        border: '3px solid transparent',
        borderRadius: '999px',
        backgroundClip: 'content-box',
      },
      '.cm-scroller::-webkit-scrollbar-track': { background: 'transparent' },

      '.cm-content': { padding: '16px 20px' },
      // The default 6px left pad would push the blockquote rule off the margin.
      '.cm-line': { padding: '0' },

      // `drawSelection` hides the native caret, so the visible one is this div.
      '.cm-cursor, .cm-dropCursor': {
        borderLeft: '2px solid var(--accent)',
        marginLeft: '-1px',
      },
      '.cm-selectionBackground': { background: 'var(--selection)' },
      '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground': {
        background: 'var(--selection)',
      },
      '.cm-content ::selection': { background: 'var(--selection)' },
    },
    { dark: opts.theme === 'dark' },
  )
}

/* --------------------------------------------------------------- highlights */

/**
 * Tag -> class map. Headings carry two classes so weight and size can be set
 * independently: a `#` marker has to drop back to normal weight while keeping
 * the heading's font size, or the text after it would no longer line up.
 */
const highlightStyle = HighlightStyle.define([
  { tag: tags.heading1, class: 'cm-md-heading cm-md-h1' },
  { tag: tags.heading2, class: 'cm-md-heading cm-md-h2' },
  { tag: tags.heading3, class: 'cm-md-heading cm-md-h3' },
  { tag: tags.heading4, class: 'cm-md-heading cm-md-h4' },
  { tag: tags.heading5, class: 'cm-md-heading cm-md-h5' },
  { tag: tags.heading6, class: 'cm-md-heading cm-md-h6' },
  { tag: tags.strong, class: 'cm-md-strong' },
  { tag: tags.emphasis, class: 'cm-md-em' },
  { tag: tags.strikethrough, class: 'cm-md-strike' },
  { tag: tags.monospace, class: 'cm-md-code' },
  { tag: tags.link, class: 'cm-md-link' },
  { tag: tags.url, class: 'cm-md-url' },
  { tag: tags.quote, class: 'cm-md-quote' },
  { tag: tags.list, class: 'cm-md-list' },
  { tag: tags.contentSeparator, class: 'cm-md-rule' },
  // GFM tags the `[ ]` / `[x]` checkbox as an atom, and the fence info string
  // and link labels as label names.
  { tag: tags.atom, class: 'cm-md-task' },
  { tag: tags.labelName, class: 'cm-md-meta' },
  { tag: tags.processingInstruction, class: 'cm-md-mark' },
])

/**
 * The parts of the visual language a highlight tag cannot express, because they
 * belong to a whole line rather than to a token:
 *
 *  - fenced and indented code want the code background edge to edge;
 *  - a blockquote wants one continuous rule down its left side, which per-token
 *    borders would render as a dashed column of stubs;
 *  - inline code wants its backticks inside the pill, and the backticks are
 *    siblings of the code text, not children.
 */
const codeLine = Decoration.line({ class: 'cm-md-codeblock' })
const quoteLine = Decoration.line({ class: 'cm-md-quoteline' })
const inlineCode = Decoration.mark({ class: 'cm-md-inline-code' })
const completedTaskMark = Decoration.mark({ class: 'cm-md-task-completed' })

function blockDecorations(view: EditorView): DecorationSet {
  const ranges: Range<Decoration>[] = []
  const { doc } = view.state
  const tree = syntaxTree(view.state)

  for (const visible of view.visibleRanges) {
    tree.iterate({
      from: visible.from,
      to: visible.to,
      enter(node) {
        if (node.name === 'InlineCode') {
          ranges.push(inlineCode.range(node.from, node.to))
          return false
        }

        const lineDeco =
          node.name === 'FencedCode' || node.name === 'CodeBlock'
            ? codeLine
            : node.name === 'Blockquote'
              ? quoteLine
              : null
        if (!lineDeco) return true

        const first = doc.lineAt(Math.max(node.from, visible.from))
        const last = doc.lineAt(Math.min(node.to, visible.to))
        for (let n = first.number; n <= last.number; n++) {
          ranges.push(lineDeco.range(doc.line(n).from))
        }
        return true
      },
    })
  }

  for (const visible of view.visibleRanges) {
    const firstLine = doc.lineAt(visible.from)
    const lastLine = doc.lineAt(visible.to)
    for (let n = firstLine.number; n <= lastLine.number; n++) {
      const line = doc.line(n)
      if (/\[x\]/i.test(line.text)) {
        ranges.push(completedTaskMark.range(line.from, line.to))
      }
    }
  }

  // Sorted on the way in: a fenced block nested in a quote emits line
  // decorations out of document order.
  return Decoration.set(ranges, true)
}

const blockStyling = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet

    constructor(view: EditorView) {
      this.decorations = blockDecorations(view)
    }

    update(update: ViewUpdate): void {
      // The tree identity check catches the case where an async parse finishes
      // without the document or the viewport having moved.
      if (
        update.docChanged ||
        update.viewportChanged ||
        syntaxTree(update.startState) !== syntaxTree(update.state)
      ) {
        this.decorations = blockDecorations(update.view)
      }
    }
  },
  { decorations: (plugin) => plugin.decorations },
)

/**
 * Order is load-bearing below. Every selector has the same specificity, so the
 * last matching rule wins, and `.cm-md-mark` sits near the bottom on purpose:
 * a marker inherits the classes of whatever it delimits, and dimming has to
 * beat all of them.
 */
const markdownStyles = EditorView.baseTheme({
  '.cm-md-heading': { fontWeight: '700' },
  '.cm-md-h1': { fontSize: '1.55em' },
  '.cm-md-h2': { fontSize: '1.3em' },
  '.cm-md-h3': { fontSize: '1.15em' },
  '.cm-md-h4, .cm-md-h5, .cm-md-h6': { fontSize: '1em' },

  '.cm-md-strong': { fontWeight: '700' },
  '.cm-md-em': { fontStyle: 'italic' },
  '.cm-md-strike': { textDecoration: 'line-through', color: 'var(--fg-muted)' },

  '.cm-md-code': { color: 'var(--fg)' },
  '.cm-md-inline-code': {
    backgroundColor: 'var(--code-bg)',
    borderRadius: '3px',
    padding: '0 3px',
  },

  '.cm-md-link': { color: 'var(--accent)', textDecoration: 'underline' },
  // A URL and a fence info string are content the user reads, not delimiters
  // they look past, so they take --fg-muted. --fg-faint is reserved below for
  // the syntax markers, where receding is the whole point.
  '.cm-md-url': { color: 'var(--fg-muted)', textDecoration: 'none' },
  '.cm-md-quote': { color: 'var(--fg-muted)' },
  '.cm-md-rule': { color: 'var(--fg-faint)' },
  '.cm-md-meta': { color: 'var(--fg-muted)' },
  '.cm-md-task': { color: 'var(--accent)' },

  '.cm-md-mark': {
    color: 'var(--fg-faint)',
    fontWeight: '400',
    textDecoration: 'none',
  },
  /*
   * The one marker that should stay visible is the list bullet. `list` is an
   * inherited tag — everything inside a list carries it — so the exclusions are
   * not paranoia: without them a `**` or a `[label]` typed inside a list item
   * would come out accent-coloured. Chained `:not()` rather than `:not(a, b)`
   * because style-mod splits selectors on commas.
   */
  '.cm-md-list.cm-md-mark:not(.cm-md-strong):not(.cm-md-em):not(.cm-md-strike):not(.cm-md-link):not(.cm-md-quote):not(.cm-md-heading):not(.cm-md-code)':
    {
      color: 'var(--accent)',
      opacity: '0.85',
    },

  '.cm-line.cm-md-codeblock': { backgroundColor: 'var(--code-bg)' },
  '.cm-line.cm-md-quoteline': {
    borderLeft: '2px solid var(--border-strong)',
    paddingLeft: '10px',
  },
  '.cm-md-task-completed': {
    textDecoration: 'line-through',
    color: 'var(--fg-muted)',
    opacity: '0.6',
  },
})

export function editorHighlight(): Extension {
  return [syntaxHighlighting(highlightStyle), blockStyling, markdownStyles]
}
